import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, {
  TenantSummary,
  BulkResult,
  bulkOperation,
  newBulkOperationId,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { BulkOperationsModal } from '../components/BulkOperationsModal';
import { TypedConfirmModal } from '../components/TypedConfirmModal';
import { BulkPlanChangeModal } from '../components/BulkPlanChangeModal';
// Welle 4.6 QoL-6: shared German status labels.
import { labelForTenantStatus } from '../services/status-labels';

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  provisioning: 'bg-blue-100 text-blue-800',
  suspending: 'bg-amber-100 text-amber-800',
  suspended: 'bg-slate-200 text-slate-700',
  reactivating: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-700',
  deprovisioning: 'bg-red-100 text-red-700',
};

// Threshold above which destructive bulk operations require a
// typed-confirmation. 5 is small enough that ad-hoc maintenance
// (e.g. a single partner region with 2-3 tenants) flows through
// uninterrupted, but large enough that an accidental "select all
// on a filtered list of 60 tenants" gets stopped at the gate.
const TYPED_CONFIRM_THRESHOLD = 5;

type BulkAction = 'suspend' | 'reactivate' | 'plan';

interface RunState {
  action: BulkAction;
  title: string;
  rows: BulkResult<unknown>[];
  running: boolean;
  /** Captured params we need to retry the failing subset. */
  planParams?: { plan: 'basic' | 'business' | 'enterprise'; reason: string };
}

export function TenantListPage() {
  const { operator } = useAuth();
  const [rows, setRows] = useState<TenantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [run, setRun] = useState<RunState | null>(null);

  const isReadOnly = !!operator?.readOnly;

  function load() {
    setLoading(true);
    api
      .get<{ rows: TenantSummary[] }>('/tenants', { params: { status: filter || undefined } })
      .then((r) => setRows(r.data.rows))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    // Reset selection whenever the filter changes — a slug that
    // was visible under "active" may not be visible under
    // "suspended", and leaving it selected would silently include
    // it in the next bulk action.
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.slug));

  function toggleRow(slug: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  function toggleAll(): void {
    setSelected((prev) => {
      // "Select all" semantics: if every visible row is selected,
      // clear; otherwise select every visible row. Selections on
      // other pages / filters are intentionally left untouched so
      // the operator can build up a cross-filter set.
      if (rows.every((r) => prev.has(r.slug))) {
        const next = new Set(prev);
        rows.forEach((r) => next.delete(r.slug));
        return next;
      }
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.slug));
      return next;
    });
  }

  // Slugs the user has selected and that are visible on the current
  // filter — guards against running an action on a stale slug that
  // no longer exists in the listing (e.g. just decommissioned).
  const selectedSlugs = useMemo(
    () => rows.filter((r) => selected.has(r.slug)).map((r) => r.slug),
    [rows, selected],
  );

  // -----------------------------------------------------------
  // Bulk runners. Every per-row call goes through the existing
  // single-tenant endpoint so audit middleware + AWX wiring fire
  // unchanged. The X-Bulk-Operation-Id header lets the audit log
  // correlate which rows were part of the same operator action
  // (consumed best-effort server-side — backend keeps working
  // without it).
  // -----------------------------------------------------------

  async function runBulk<TResult>(
    action: BulkAction,
    title: string,
    targetSlugs: string[],
    op: (slug: string, headers: Record<string, string>) => Promise<TResult>,
    planParams?: RunState['planParams'],
  ): Promise<void> {
    if (targetSlugs.length === 0) return;
    const bulkId = newBulkOperationId();
    const headers = { 'X-Bulk-Operation-Id': bulkId };

    const seed: BulkResult<unknown>[] = targetSlugs.map((slug) => ({ slug, status: 'queued' }));
    setRun({ action, title, rows: seed, running: true, planParams });

    const results = await bulkOperation<TResult>(
      targetSlugs,
      (slug) => op(slug, headers),
      {
        concurrency: 5,
        onUpdate: (entry, idx) => {
          setRun((prev) => {
            if (!prev) return prev;
            const nextRows = prev.rows.slice();
            nextRows[idx] = entry as BulkResult<unknown>;
            return { ...prev, rows: nextRows };
          });
        },
      },
    );

    setRun((prev) =>
      prev ? { ...prev, rows: results as BulkResult<unknown>[], running: false } : prev,
    );
    // Refresh the listing so the operator sees the freshly applied
    // status / plan transitions without a manual reload.
    load();
  }

  async function suspendSlugs(slugs: string[]): Promise<void> {
    await runBulk(
      'suspend',
      `Suspendiere ${slugs.length} Tenant(s)`,
      slugs,
      (slug, headers) => api.post(`/tenants/${slug}/suspend`, {}, { headers }),
    );
  }

  async function reactivateSlugs(slugs: string[]): Promise<void> {
    await runBulk(
      'reactivate',
      `Reaktiviere ${slugs.length} Tenant(s)`,
      slugs,
      (slug, headers) => api.post(`/tenants/${slug}/reactivate`, {}, { headers }),
    );
  }

  async function planChangeSlugs(
    slugs: string[],
    plan: 'basic' | 'business' | 'enterprise',
    reason: string,
  ): Promise<void> {
    await runBulk(
      'plan',
      `Plan-Wechsel auf "${plan}" für ${slugs.length} Tenant(s)`,
      slugs,
      (slug, headers) => api.put(`/tenants/${slug}/plan`, { plan, reason }, { headers }),
      { plan, reason },
    );
  }

  function onSuspendClick(): void {
    if (selectedSlugs.length === 0) return;
    // Suspend is destructive enough that we want a typed confirm
    // once it's a real batch. Single-digit selections go through
    // immediately to keep the common operator flow snappy.
    if (selectedSlugs.length > TYPED_CONFIRM_THRESHOLD) {
      setConfirmSuspend(true);
    } else {
      void suspendSlugs(selectedSlugs);
    }
  }

  function onReactivateClick(): void {
    if (selectedSlugs.length === 0) return;
    void reactivateSlugs(selectedSlugs);
  }

  function onRetryFailed(failedSlugs: string[]): void {
    if (!run) return;
    if (run.action === 'suspend') void suspendSlugs(failedSlugs);
    else if (run.action === 'reactivate') void reactivateSlugs(failedSlugs);
    else if (run.action === 'plan' && run.planParams) {
      void planChangeSlugs(failedSlugs, run.planParams.plan, run.planParams.reason);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Deployments ({rows.length})</h2>
        <div className="flex items-center gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-2 py-1 rounded border">
            <option value="">Alle Status</option>
            <option value="active">active</option>
            <option value="provisioning">provisioning</option>
            <option value="suspended">suspended</option>
            <option value="failed">failed</option>
          </select>
          <Link to="/tenants/new" className="btn-primary">Deployment anlegen</Link>
        </div>
      </div>

      {selectedSlugs.length > 0 && (
        <div
          role="region"
          aria-label="Bulk-Aktionen"
          className="bg-slate-50 border rounded p-3 mb-3 flex items-center gap-3 flex-wrap"
        >
          <span className="text-sm font-medium">
            {selectedSlugs.length} ausgewählt
          </span>
          <button
            onClick={onSuspendClick}
            disabled={isReadOnly}
            className="btn-danger disabled:opacity-50"
            title={isReadOnly ? 'Read-only-Operator' : 'Ausgewählte Tenants suspendieren'}
          >
            Suspendieren
          </button>
          <button
            onClick={onReactivateClick}
            disabled={isReadOnly}
            className="btn-primary disabled:opacity-50"
            title={isReadOnly ? 'Read-only-Operator' : 'Ausgewählte Tenants reaktivieren'}
          >
            Reaktivieren
          </button>
          <button
            onClick={() => setPlanDialogOpen(true)}
            disabled={isReadOnly}
            className="px-3 py-1 rounded border disabled:opacity-50"
          >
            Plan ändern…
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="text-sm text-slate-500 hover:underline ml-auto"
          >
            Auswahl zurücksetzen
          </button>
        </div>
      )}

      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded">
          <thead className="bg-slate-100 text-left text-sm uppercase tracking-wide">
            <tr>
              <th className="p-3 w-10">
                <input
                  type="checkbox"
                  aria-label="Alle auf dieser Seite auswählen"
                  checked={allOnPageSelected}
                  onChange={toggleAll}
                  disabled={rows.length === 0}
                />
              </th>
              <th className="p-3">Slug</th>
              <th className="p-3">Name</th>
              <th className="p-3">Plan</th>
              <th className="p-3">Status</th>
              <th className="p-3">Admin</th>
              <th className="p-3">Erstellt</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {rows.map((t) => (
              <tr key={t.id} className="border-t hover:bg-slate-50">
                <td className="p-3">
                  <input
                    type="checkbox"
                    aria-label={`Tenant ${t.slug} auswählen`}
                    checked={selected.has(t.slug)}
                    onChange={() => toggleRow(t.slug)}
                  />
                </td>
                <td className="p-3 font-mono">
                  <Link to={`/tenants/${t.slug}`} className="text-primary-600 hover:underline">{t.slug}</Link>
                </td>
                <td className="p-3">{t.name}</td>
                <td className="p-3"><span className="badge bg-slate-200">{t.plan}</span></td>
                <td className="p-3">
                  <span className={`badge ${STATUS_COLORS[t.status] ?? 'bg-slate-200'}`}>{labelForTenantStatus(t.status)}</span>
                </td>
                <td className="p-3">{t.adminEmail}</td>
                <td className="p-3 text-slate-500">{new Date(t.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Keine Tenants.</td></tr>}
          </tbody>
        </table>
      )}

      {confirmSuspend && (
        <TypedConfirmModal
          title={`${selectedSlugs.length} Tenants suspendieren?`}
          description="Suspend stoppt die Tenant-Workloads und triggert AWX-Cluster-Templates. Plan-Änderungen während Suspend sind blockiert."
          confirmPhrase="SUSPEND"
          confirmLabel="Suspendieren"
          affectedSlugs={selectedSlugs}
          onCancel={() => setConfirmSuspend(false)}
          onConfirm={() => {
            setConfirmSuspend(false);
            void suspendSlugs(selectedSlugs);
          }}
        />
      )}

      {planDialogOpen && (
        <BulkPlanChangeModal
          affectedSlugs={selectedSlugs}
          onCancel={() => setPlanDialogOpen(false)}
          onConfirm={(plan, reason) => {
            setPlanDialogOpen(false);
            void planChangeSlugs(selectedSlugs, plan, reason);
          }}
        />
      )}

      {run && (
        <BulkOperationsModal
          title={run.title}
          rows={run.rows}
          running={run.running}
          onClose={() => setRun(null)}
          onRetryFailed={onRetryFailed}
        />
      )}
    </div>
  );
}
